import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { MatError } from '@angular/material/input';
import { formatDisplayText } from '../../utils/text-case.utils';
import { environment } from '../../../../../../../../projects/my-app/src/environments/environment';
import { formatNumber } from '../../self-service-visualization/shared/number-format.utils';

interface SliderComputationColumn {
  name: string;
  type?: string;
}

interface SliderComputationEntry {
  id: string;
  expression: string;
  alias: string;
  raw: string;
}

interface SliderNumericRangeFilter {
  column: string;
  absoluteMin: number;
  absoluteMax: number;
  currentMin: number;
  currentMax: number;
}
@Component({
  selector: 'app-mat-slider',
  standalone: true,
  imports: [CommonModule, MatSliderModule, FormsModule, MatInputModule],
  templateUrl: './mat-slider.component.html',
  styleUrl: './mat-slider.component.scss',
})
export class MatSliderComponent implements OnInit, OnChanges {
  @Input() data!: any; // input like bar-chart
  @Output() valueChange = new EventEmitter<{ start: number; end: number }>();

  min!: number;
  max!: number;
  step!: number;
  label!: string;

  rangeStart!: number;
  rangeEnd!: number;
  errorMsg: string = '';

  initialRangeStart!: number;
  initialRangeEnd!: number;

  computeOpen = false;
  currentExpression = '';
  currentAlias = '';
  expressionError = '';
  computationErrorMessage = '';
  computationSuccessMessage = '';
  computationLoading = false;
  computationResultPreview: unknown = null;
  computations: SliderComputationEntry[] = [];
  numericFilters: SliderNumericRangeFilter[] = [];
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnInit() {
    if (!this.data) {
      console.error('Slider data not provided');
      return;
    }

    this.configService.getConfig(this.data?.apiUrl, this.data).subscribe({
      next: (res) => {
        this.min = res?.data?.min;
        this.max = res?.data?.max;
        this.step = res?.data?.step;
        // UI Standards (Point 3): filter label in Capital Case unless overridden
        const rawName = res?.data?.name ?? 'Slider';
        const mode = (this.data?.textCase || 'capital') as any;
        this.label = formatDisplayText(rawName, mode);

        // Take rangeStart from config and fix it
        this.rangeStart = this.roundIncomingValue(res?.data?.rangeStart ?? this.min);
        this.rangeEnd = this.roundIncomingValue(res?.data?.rangeEnd ?? this.max);

        this.initialRangeStart = this.rangeStart;
        this.initialRangeEnd = this.rangeEnd;
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  /** The initial value comes from `ngOnInit`'s async `configService.getConfig`
   *  call — but that only ever runs once, so a target slider's value being
   *  pushed in later (a driving input control's recompute result) would
   *  silently never show up. Re-sync directly from the new `data` on every
   *  SUBSEQUENT change (the first change is the initial binding, already
   *  handled by ngOnInit's own load). */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']?.firstChange) return;
    // The self-service tile shape nests the config one level deeper
    // (`data.data.{rangeStart,rangeEnd}` — the same object ngOnInit's
    // getConfig unwrap reads from); other hosts pass `data.{rangeStart,…}`
    // directly. Read the deeper shape first, else pushed values (a driving
    // input's recompute result) land where this re-sync never looks.
    const config = (this.data?.data?.data ?? this.data?.data) as
      | { rangeStart?: unknown; rangeEnd?: unknown }
      | undefined;
    const nextRangeEnd = config?.rangeEnd;
    if (nextRangeEnd !== undefined && nextRangeEnd !== null) {
      this.rangeEnd = this.roundIncomingValue(nextRangeEnd);
    }
    const nextRangeStart = config?.rangeStart;
    if (nextRangeStart !== undefined && nextRangeStart !== null) {
      this.rangeStart = this.roundIncomingValue(nextRangeStart);
    }
  }

  /** Round a PUSHED value (recompute result / initial default) to the Format
   *  popup's `decimals` before it ever reaches the editable field — a computed
   *  value like `0.6118790192803075` showed up verbatim otherwise, decimals
   *  setting or not. Only applies to values we're SETTING from outside; never
   *  touches what the user is actively dragging/typing. */
  private roundIncomingValue(raw: unknown): number {
    const num = Number(raw);
    const d = Number(this.data?.decimals);
    if (!Number.isFinite(num) || !Number.isFinite(d)) return num;
    return Number(num.toFixed(Math.max(0, Math.min(6, Math.floor(d)))));
  }

  reset() {
    this.rangeStart = this.initialRangeStart;
    this.rangeEnd = this.initialRangeEnd;
    this.errorMsg = '';
    this.onRangeChange();
  }

  validateAndEmit() {
    const value = Number(this.rangeEnd);

    if (
      value === null ||
      value === undefined ||
      isNaN(value) ||
      value < this.min ||
      value > this.max
    ) {
      this.errorMsg = `Please enter a value between ${this.min} and ${this.max}`;
      return;
    }

    this.errorMsg = '';
    this.onRangeChange();
  }

  onRangeChange() {
    const value = Number(this.rangeEnd);

    if (
      value === null ||
      value === undefined ||
      isNaN(value) ||
      value < this.min ||
      value > this.max
    ) {
      return;
    }

    this.valueChange.emit({ start: this.rangeStart, end: this.rangeEnd });

    if (this.data?.key) {
      this.globalFilterService.updateFilterSelection(
        this.data.key,
        [this.rangeStart, this.rangeEnd],
        this.data.isQueryParam,
      );
    }
  }
  /** Read-only formatted preview of the current value (scale + decimals from
   *  the DVM builder's Format popup: numberFormat/decimals) — shown ALONGSIDE
   *  the editable number field, which stays raw/unscaled so typing an exact
   *  value keeps working. Hidden when no scale/decimals is configured. */
  get formattedValue(): string | null {
    const numberFormat = this.data?.numberFormat;
    if (!numberFormat || numberFormat === 'NONE') return null;
    if (this.rangeEnd === undefined || this.rangeEnd === null || Number.isNaN(Number(this.rangeEnd))) return null;
    return formatNumber(Number(this.rangeEnd), numberFormat, this.data?.decimals);
  }

  get entityName(): string {
    return this.resolveString(this.data?.computationEntityName, this.data?.entityName, this.data?.datasetName, '');
  }

  get allColumns(): SliderComputationColumn[] {
    const columns = this.data?.computationColumns;
    return Array.isArray(columns) ? columns : [];
  }

  get numericColumns(): SliderComputationColumn[] {
    return this.allColumns.filter((column) => column.type === 'number');
  }

  get canCompute(): boolean {
    // `hideCompute` on the widget data suppresses the on-the-fly compute (bolt)
    // button — self-service tiles that shouldn't expose it set this flag.
    if (this.data?.hideCompute) return false;
    return !!this.entityName || this.allColumns.length > 0;
  }

  toggleCompute(event: MouseEvent): void {
    event.stopPropagation();
    this.computeOpen = !this.computeOpen;
    if (this.computeOpen) {
      this.computationErrorMessage = '';
      this.computationSuccessMessage = '';
    }
  }

  closeCompute(): void {
    this.computeOpen = false;
  }

  appendColumn(column: SliderComputationColumn): void {
    this.currentExpression = this.currentExpression
      ? `${this.currentExpression} + ${column.name}`
      : column.name;
    if (!this.currentAlias) {
      this.currentAlias = column.name;
    }
    this.expressionError = '';
  }

  addComputation(): void {
    const expression = this.currentExpression.trim();
    const alias = this.currentAlias.trim();
    if (!expression) {
      this.expressionError = 'Expression is required.';
      return;
    }

    this.expressionError = '';
    this.computations = [
      ...this.computations,
      {
        id: `comp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        expression,
        alias: alias || expression,
        raw: alias ? `${expression} as ${alias}` : expression,
      },
    ];
    this.currentExpression = '';
    this.currentAlias = '';
  }

  removeComputation(id: string): void {
    this.computations = this.computations.filter((entry) => entry.id !== id);
  }

  toggleFilter(column: SliderComputationColumn): void {
    if (this.hasFilter(column.name)) {
      this.numericFilters = this.numericFilters.filter((filter) => filter.column !== column.name);
      return;
    }

    const values = this.computationRows
      .map((row) => Number(row?.[column.name]))
      .filter((value) => Number.isFinite(value));
    const absoluteMin = values.length ? Math.min(...values) : 0;
    const absoluteMax = values.length ? Math.max(...values) : 100;
    this.numericFilters = [
      ...this.numericFilters,
      { column: column.name, absoluteMin, absoluteMax, currentMin: absoluteMin, currentMax: absoluteMax },
    ];
  }

  hasFilter(columnName: string): boolean {
    return this.numericFilters.some((filter) => filter.column === columnName);
  }

  updateFilterValue(filter: SliderNumericRangeFilter, key: 'currentMin' | 'currentMax', event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(value)) return;
    this.numericFilters = this.numericFilters.map((current) => {
      if (current.column !== filter.column) return current;
      const next = { ...current, [key]: value };
      if (next.currentMin > next.currentMax) return current;
      return next;
    });
  }

  removeFilter(columnName: string): void {
    this.numericFilters = this.numericFilters.filter((filter) => filter.column !== columnName);
  }

  clearComputations(): void {
    this.computations = [];
    this.numericFilters = [];
    this.currentExpression = '';
    this.currentAlias = '';
    this.expressionError = '';
    this.computationErrorMessage = '';
    this.computationSuccessMessage = '';
    this.computationResultPreview = null;
  }

  applyComputations(): void {
    if (!this.entityName) {
      this.computationErrorMessage = 'No entity name found for this slider.';
      return;
    }
    if (!this.computations.length && !this.numericFilters.length) {
      this.computationErrorMessage = 'Add at least one computation or range filter.';
      return;
    }

    this.computationLoading = true;
    this.computationErrorMessage = '';
    this.computationSuccessMessage = '';

    this.http.post<Record<string, unknown>>(`${String(environment.URL || '').replace(/\/$/, '')}/getData`, {
      entityName: this.entityName,
      attributesList: this.computations.map((entry) => entry.raw),
      filterList: this.numericFilters.map((filter) => ({
        column: filter.column,
        operator: 'BETWEEN',
        value: [filter.currentMin, filter.currentMax],
      })),
    }).subscribe({
      next: (result) => {
        this.computationLoading = false;
        this.computationSuccessMessage = 'Data fetched successfully.';
        this.computationResultPreview = result;
      },
      error: (err) => {
        this.computationLoading = false;
        this.computationErrorMessage = err?.error?.message || err?.message || 'Failed to fetch data.';
      },
    });
  }

  trackByComputation(_index: number, entry: SliderComputationEntry): string {
    return entry.id;
  }

  trackByFilter(_index: number, entry: SliderNumericRangeFilter): string {
    return entry.column;
  }

  private get computationRows(): Array<Record<string, unknown>> {
    const rows = this.data?.computationRows;
    return Array.isArray(rows) ? rows : [];
  }

  private resolveString(...values: unknown[]): string {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }
}

